import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { getDojos } from "../../../services/dojoServices";
import { getGroupsList } from "../../../services/groupServices";
import {
  getAllAdherentsListWithGroup,
  getBirthdayAdherents,
} from "../../../services/adherentServices";
import { getSettings } from "../../../services/settingsServices";
import {
  getAllAttendances,
  countAttendance,
  deleteAllAttendances,
  setAttendance as apiSetAttendance,
  clearAttendance as apiClearAttendance,
} from "../../../services/attendanceServices";
import DashboardNavigation from "../../DashboardNavigation/DashboardNavigation";
import { useUser } from "../../Home/UserContext";
import { getRoleFromRoleId } from "../../../utils/roleHelper";
import useScrollToTop from "../../../hooks/useScollToTop";
import { ToastContainer, toast } from "react-toastify";
import FiltersBar from "./FiltersBar";
import AdherentGrid from "./AdherentGrid";
import StatsBar from "./StatsBar";
import HeaderTitle from "../../../components/HeaderTitle/HeaderTitle";
import { FaUserCheck } from "react-icons/fa";

const AttendancePage = () => {
  useScrollToTop();

  // ------- State principaux -------
  const [dojos, setDojos] = useState([]);
  const [selectedDojo, setSelectedDojo] = useState("");
  const [groups, setGroups] = useState([]);

  const [adherents, setAdherents] = useState([]);
  const [birthdayAdherents, setBirthdayAdherents] = useState([]);
  const [assiduityInfo, setAssiduityInfo] = useState({}); // { [adherentId]: { assiduity, totalAttendances, total } }

  const [selectedSeason, setSelectedSeason] = useState("");
  const [seasons, setSeasons] = useState([]);

  const [filterGroup, setFilterGroup] = useState("");
  const [statutInscription, setStatutInscription] = useState("");
  const [selectedDate, setSelectedDate] = useState(
    () => new Date().toISOString().split("T")[0],
  );
  const [hideLicencieArret, setHideLicencieArret] = useState(true);
  const [sortAssiduity, setSortAssiduity] = useState(false);

  const [loading, setLoading] = useState(true);

  // Présences/absences pour la date sélectionnée
  // 0 = neutre, 1 = présent, 2 = absent
  const [attendance, setAttendance] = useState({});
  const attendanceRef = useRef(attendance);

  // Cache local des attendances pour éviter de tout recharger après chaque clic
  const [allAttendancesCache, setAllAttendancesCache] = useState(null);
  const [savingOne, setSavingOne] = useState(false);

  const { user } = useUser();
  const userRole = getRoleFromRoleId(user?.role_id);
  const isAdmin = userRole === "admin";

  // ------- Helpers -------
  const getDefaultPhoto = (genre) =>
    genre === "Masculin"
      ? "/img/judoka_avatar.png"
      : "/img/judokate_avatar.png";

  const buildDailyMap = useCallback((dateISO, adherentIds, allAtts) => {
    const ymd = String(dateISO).slice(0, 10);
    const setIds = new Set(adherentIds);
    const map = {};
    adherentIds.forEach((id) => (map[id] = 0));
    (allAtts || []).forEach((a) => {
      const sameDay = String(a.date_presence).slice(0, 10) === ymd;
      if (sameDay && setIds.has(a.adherent_id)) {
        map[a.adherent_id] = a.presence ? 1 : 2;
      }
    });
    return map;
  }, []);

  // ------- Initial fetch : settings + dojos + adherents + allAttendances -------
  useEffect(() => {
    const init = async () => {
      try {
        setLoading(true);
        const [settings, dojoList, adherentList, birthdayList, allAtts] =
          await Promise.all([
            getSettings(),
            getDojos(),
            getAllAdherentsListWithGroup(),
            getBirthdayAdherents(),
            getAllAttendances(), // cache initial
          ]);

        // Saison
        if (settings && settings.length > 0) {
          const activeSeason = settings[0].saison_active;
          const prev = (s) => {
            const [a, b] = s.split("-").map(Number);
            return `${a - 1}-${b - 1}`;
          };
          const next = (s) => {
            const [a, b] = s.split("-").map(Number);
            return `${a + 1}-${b + 1}`;
          };
          setSeasons([prev(activeSeason), activeSeason, next(activeSeason)]);
          setSelectedSeason(activeSeason);
        }

        // Dojos + groupes
        setDojos(dojoList || []);
        let g = await getGroupsList();
        if (dojoList?.length === 1) {
          setSelectedDojo(String(dojoList[0].id));
          g = g.filter((GG) => GG.dojo_id === dojoList[0].id);
        }
        setGroups(g);

        // Adhérents
        setAdherents(Array.isArray(adherentList) ? adherentList : []);
        setBirthdayAdherents(Array.isArray(birthdayList) ? birthdayList : []);

        // Cache attendances complet + état du jour pour la vue courante
        setAllAttendancesCache(allAtts || []);
        const ids = (adherentList || []).map((a) => a.id);
        const initialMap = buildDailyMap(selectedDate, ids, allAtts || []);
        setAttendance(initialMap);
        attendanceRef.current = initialMap;
      } catch (e) {
        console.error(e);
        toast.error("Erreur de chargement des données.");
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [buildDailyMap, selectedDate]);

  // ------- Filtrage + tri -------
  const filteredAdherents = useMemo(() => {
    const allowedGroupNames = new Set((groups || []).map((g) => g.nom));

    const inDojo = (a) =>
      !selectedDojo ||
      (Array.isArray(a?.Groups) &&
        a.Groups.some((g) => allowedGroupNames.has(g.nom)));

    const inGroup = (a) =>
      !filterGroup ||
      (Array.isArray(a?.Groups) && a.Groups.some((g) => g.nom === filterGroup));

    const inSeason = (a) =>
      !selectedSeason || a?.saison_active === selectedSeason;

    const inStatus = (a) =>
      !statutInscription || a?.statut_inscription === statutInscription;

    const notLicencieArret = (a) =>
      !hideLicencieArret || a?.statut_inscription !== "Licencié - Arrêt";

    let list = (adherents || []).filter(
      (a) =>
        inDojo(a) &&
        inGroup(a) &&
        inSeason(a) &&
        inStatus(a) &&
        notLicencieArret(a),
    );

    if (sortAssiduity) {
      list = list.slice().sort((aa, bb) => {
        const A = assiduityInfo[aa.id]?.assiduity ?? 0;
        const B = assiduityInfo[bb.id]?.assiduity ?? 0;
        return B - A;
      });
    } else {
      list = list
        .slice()
        .sort((aa, bb) =>
          String(aa.nom || "").localeCompare(String(bb.nom || ""), "fr"),
        );
    }

    return list;
  }, [
    adherents,
    groups,
    selectedDojo,
    filterGroup,
    selectedSeason,
    statutInscription,
    hideLicencieArret,
    sortAssiduity,
    assiduityInfo,
  ]);

  // ------- Quand date OU la liste affichée change : reconstruire la map du jour -------
  useEffect(() => {
    if (!allAttendancesCache) return;
    const ids = filteredAdherents.map((a) => a.id);
    const map = buildDailyMap(selectedDate, ids, allAttendancesCache);
    // On fusionne avec les anciennes clés pour ne pas exploser l'objet
    setAttendance((prev) => {
      const next = { ...prev };
      ids.forEach((id) => (next[id] = map[id] ?? 0));
      attendanceRef.current = next;
      return next;
    });
  }, [selectedDate, filteredAdherents, allAttendancesCache, buildDailyMap]);

  // ------- Chargement paresseux de l’assiduité sur la page -------
  const loadAssiduity = useCallback(
    async (ids) => {
      const toLoad = ids.filter((id) => !assiduityInfo[id]);
      if (toLoad.length === 0) return;

      try {
        const promises = toLoad.map((id) =>
          countAttendance(id)
            .then((data) => ({ id, data }))
            .catch(() => ({ id, data: null })),
        );
        const results = await Promise.all(promises);
        setAssiduityInfo((prev) => {
          const next = { ...prev };
          results.forEach(({ id, data }) => {
            if (data) next[id] = data;
          });
          return next;
        });
      } catch {
        // silencieux
      }
    },
    [assiduityInfo],
  );

  // ------- Clic sur une carte = édition immédiate (0 -> 1 -> 2 -> 0) -------
  const toggleAttendanceImmediate = useCallback(
    async (adherentId) => {
      if (loading || savingOne) return;

      const prevVal = attendanceRef.current[adherentId] ?? 0;
      const nextVal = prevVal === 0 ? 1 : prevVal === 1 ? 2 : 0;

      // Optimiste local
      setAttendance((prev) => {
        const next = { ...prev, [adherentId]: nextVal };
        attendanceRef.current = next;
        return next;
      });

      try {
        setSavingOne(true);
        if (nextVal === 1) {
          // Présent
          await apiSetAttendance(adherentId, selectedDate, true);
          // MAJ cache
          setAllAttendancesCache((prev) => {
            const iso = String(selectedDate).slice(0, 10);
            const others =
              (prev || []).filter(
                (a) =>
                  !(
                    a.adherent_id === adherentId &&
                    String(a.date_presence).slice(0, 10) === iso
                  ),
              ) || [];
            return [
              ...others,
              { adherent_id: adherentId, presence: true, date_presence: iso },
            ];
          });
        } else if (nextVal === 2) {
          // Absent
          await apiSetAttendance(adherentId, selectedDate, false);
          setAllAttendancesCache((prev) => {
            const iso = String(selectedDate).slice(0, 10);
            const others =
              (prev || []).filter(
                (a) =>
                  !(
                    a.adherent_id === adherentId &&
                    String(a.date_presence).slice(0, 10) === iso
                  ),
              ) || [];
            return [
              ...others,
              { adherent_id: adherentId, presence: false, date_presence: iso },
            ];
          });
        } else {
          // 0 => suppression
          await apiClearAttendance(adherentId, selectedDate);
          setAllAttendancesCache((prev) => {
            const iso = String(selectedDate).slice(0, 10);
            return (prev || []).filter(
              (a) =>
                !(
                  a.adherent_id === adherentId &&
                  String(a.date_presence).slice(0, 10) === iso
                ),
            );
          });
        }
      } catch (e) {
        // Rollback
        setAttendance((prev) => {
          const next = { ...prev, [adherentId]: prevVal };
          attendanceRef.current = next;
          return next;
        });
        console.error(e);
        toast.error("Échec de la mise à jour.");
      } finally {
        setSavingOne(false);
      }
    },
    [loading, savingOne, selectedDate],
  );

  // Compteurs (uniquement sur les adhérents affichés)
  const presentCount = useMemo(() => {
    const ids = new Set(filteredAdherents.map((a) => a.id));
    return Object.entries(attendance).reduce(
      (acc, [k, v]) => (ids.has(Number(k)) && v === 1 ? acc + 1 : acc),
      0,
    );
  }, [attendance, filteredAdherents]);

  const absentCount = useMemo(() => {
    const ids = new Set(filteredAdherents.map((a) => a.id));
    return Object.entries(attendance).reduce(
      (acc, [k, v]) => (ids.has(Number(k)) && v === 2 ? acc + 1 : acc),
      0,
    );
  }, [attendance, filteredAdherents]);

  // Anniv rapide
  const birthdaySet = useMemo(
    () => new Set(birthdayAdherents.map((a) => a.id)),
    [birthdayAdherents],
  );

  return (
    <section className="w-full">
      {/* Offset nav */}
      <div className="pt-[150px]" aria-hidden />

      <div className="mx-auto w-full max-w-7xl px-3 sm:px-6">
        {/* Header page */}

        <HeaderTitle
          role="Enseignant"
          roleId="3"
          title="Gestion des Appels"
          subtitle="Gérez les présences de vos adhérents en temps réel."
          icon={FaUserCheck}
          align="left"
          className="mb-4"
        />

        {/* Navigation */}
        <div className="mt-3 mb-6">
          <DashboardNavigation role={userRole} />
        </div>

        <div className="mx-auto w-full max-w-7xl px-3 sm:px-6">
          {/* Carte principale */}
          <div className="rounded-2xl overflow-hidden shadow ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/80 dark:bg-slate-900/60 backdrop-blur">
            {/* Bandeau dégradé */}
            <div className="bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 px-5 sm:px-8 py-6 text-white">
              <h1 className="text-lg sm:text-xl font-semibold text-center">
                Présence des Adhérents
              </h1>
              <p className="text-sm/6 opacity-95 text-center mt-1">
                Touchez une carte pour basculer : Non saisi → Présent → Absent →
                Non saisi. Enregistrement immédiat par adhérent.
              </p>
            </div>

            {/* Filtres */}
            <div className="px-5 sm:px-8 pb-4">
              <FiltersBar
                loading={loading}
                // dojo / groupe
                dojos={dojos}
                selectedDojo={selectedDojo}
                onDojoChange={async (val) => {
                  setSelectedDojo(val);
                  const allGroups = await getGroupsList();
                  setGroups(
                    val
                      ? allGroups.filter(
                          (g) => String(g.dojo_id) === String(val),
                        )
                      : allGroups,
                  );
                  setFilterGroup("");
                }}
                groups={groups}
                filterGroup={filterGroup}
                onGroupChange={setFilterGroup}
                // saison / statut / date
                seasons={seasons}
                selectedSeason={selectedSeason}
                onSeasonChange={setSelectedSeason}
                statutInscription={statutInscription}
                onStatutChange={setStatutInscription}
                selectedDate={selectedDate}
                onDateChange={(d) => {
                  setSelectedDate(d);
                  // la map du jour est reconstruite via useEffect + cache
                }}
                // options
                hideLicencieArret={hideLicencieArret}
                onToggleHideLicencie={setHideLicencieArret}
                sortAssiduity={sortAssiduity}
                onToggleSortAssiduity={setSortAssiduity}
                // danger
                onDeleteAll={isAdmin ? deleteAllAttendances : undefined}
              />
            </div>

            {/* Grille adhérents */}
            <div className="px-5 sm:px-8 pb-6">
              {filterGroup ? (
                <AdherentGrid
                  adherents={filteredAdherents}
                  pageSize={24}
                  attendance={attendance}
                  onToggle={toggleAttendanceImmediate}
                  assiduityInfo={assiduityInfo}
                  requestAssiduity={(ids) => {
                    // charge l’assiduité en lazy
                    loadAssiduity(ids);
                  }}
                  birthdaySet={birthdaySet}
                  getDefaultPhoto={getDefaultPhoto}
                />
              ) : (
                <div className="text-center py-10 text-slate-600 dark:text-slate-400">
                  Sélectionnez un dojo et un groupe pour commencer.
                </div>
              )}
            </div>

            {/* Stats */}
            <div className="px-5 sm:px-8 pb-4">
              <StatsBar present={presentCount} absent={absentCount} />
            </div>
          </div>
        </div>
      </div>

      <ToastContainer />
    </section>
  );
};

export default AttendancePage;
